import { useI18n } from "@wojo/localization";
import { FormCheckboxGroup, Text } from "@wojo/ui";
import React, { useState } from "react";
import {
    DomainError,
    GcDepartureRuleLevel,
    GcDepartureRuleLineItemType,
    TripBuilderDraftOrderWithFlightOffersDocument,
    useTripBuilderAddExtensionMutation,
    useTripBuilderRemoveExtensionMutation,
} from "../../../../generated/graphql";
import { getCurrency } from "../../../../hooks";
import { useTripBuilder } from "../context/TripBuilderContext";
import { useTripBuilderQuote } from "../context/TripBuilderQuoteStepContext";
import { useTripBuilderAnalytics } from "../hooks/use-trip-builder-analytics";
import { QuoteExtensionsModal } from "./QuoteExtensionsModal";
import styles from "./TripBuilderQuoteStep.module.scss";
import { useExtensionPrices } from "../hooks/use-extension-prices";
import { PriceComparison } from "../../../../price-comparison";
import currency from "currency.js";

export const QuoteExtensions: React.FC = () => {
    const { departure, gcDeparture } = useTripBuilder();
    const { draftQuote } = useTripBuilderQuote();
    const { addToCart, removeFromCart } = useTripBuilderAnalytics();
    const { formattedPrice, price, formatCurrency } = getCurrency();
    const { productPage } = useI18n("productPage");
    const extension = departure?.activeExtension;
    const content = departure?.activeExtensionContent;
    const [addExtension, addResult] = useTripBuilderAddExtensionMutation();
    const [removeExtension, removeResult] =
        useTripBuilderRemoveExtensionMutation();
    const isEditing = addResult.loading || removeResult.loading;
    const [error, setError] = useState<string | undefined>(undefined);
    const {
        getExtensionSalePrice,
        getTotalExtensionSalePrice,
        getAllExtensionSalePrices,
    } = useExtensionPrices();

    if (!extension) {
        return null;
    }

    const isRequired = gcDeparture?.lineItemRules?.some(
        (rule) =>
            rule.lineItemType === GcDepartureRuleLineItemType.Extension &&
            rule.level === GcDepartureRuleLevel.Required &&
            rule.lineItemCode === extension.lineItemCode,
    );
    const isRestricted = gcDeparture?.lineItemRules?.some(
        (rule) =>
            rule.lineItemType === GcDepartureRuleLineItemType.Extension &&
            rule.level === GcDepartureRuleLevel.Restricted &&
            rule.lineItemCode === extension.lineItemCode,
    );
    const isRecommended = gcDeparture?.lineItemRules?.some(
        (rule) =>
            rule.lineItemType === GcDepartureRuleLineItemType.Extension &&
            rule.level === GcDepartureRuleLevel.Recommended &&
            rule.lineItemCode === extension.lineItemCode,
    );

    if (isRestricted) {
        return null;
    }

    const { extensionSalePrice, appliedToTrip } = getExtensionSalePrice();

    const originalPrice = price(extension.price.value).value;
    const totalPrice =
        getTotalExtensionSalePrice() ??
        currency(originalPrice).multiply(draftQuote?.order.trips.length || 1)
            .value;

    const gtmItems = getAllExtensionSalePrices().map((salePrice) => {
        return {
            item_id: extension.lineItemCode,
            item_name: content?.name || "",
            item_variant: "Extension",
            price: salePrice ?? originalPrice,
            discount: currency(originalPrice).subtract(
                salePrice ?? originalPrice,
            ).value,
            quantity: 1,
        };
    });

    const handleAddExtension = (extensionCode: string) => {
        if (draftQuote?._id) {
            addToCart(totalPrice, gtmItems, {
                ga4_object: "TRIP_BUILDER",
                ga4_action: "EXTENSION_SELECTED",
                state: "selected",
            });
            addExtension({
                variables: {
                    id: draftQuote._id,
                    lineItemCode: extensionCode,
                },
                refetchQueries: [TripBuilderDraftOrderWithFlightOffersDocument],
                onCompleted: (data) => {
                    if (
                        data.addDraftOrderExtension.__typename !== "DraftOrder"
                    ) {
                        setError(
                            (data.addDraftOrderExtension as DomainError)
                                .message,
                        );
                    }
                },
            });
        }
    };

    const handleRemoveExtension = () => {
        if (draftQuote?._id) {
            removeFromCart(totalPrice, gtmItems, {
                ga4_object: "TRIP_BUILDER",
                ga4_action: "EXTENSION_SELECTED",
                state: "notSelected",
            });
            removeExtension({
                variables: {
                    id: draftQuote._id,
                },
                refetchQueries: [TripBuilderDraftOrderWithFlightOffersDocument],
                onCompleted: (data) => {
                    if (
                        data.removeDraftOrderExtension.__typename !==
                        "DraftOrder"
                    ) {
                        setError(
                            (data.removeDraftOrderExtension as DomainError)
                                .message,
                        );
                    }
                },
            });
        }
    };

    return (
        <div className={styles["form-field"]}>
            <FormCheckboxGroup
                disabled={isRequired || isEditing}
                title={
                    <Text.Body variant="2" emphasis="2" tag="span">
                        {productPage.tripBuilder.quote.extension.heading.l()}
                    </Text.Body>
                }
                description={
                    <QuoteExtensionsModal
                        content={content}
                        formattedPrice={
                            <PriceComparison
                                fullPrice={formattedPrice(
                                    extension.price.value,
                                )}
                                salePrice={
                                    extensionSalePrice
                                        ? formatCurrency(extensionSalePrice)
                                        : undefined
                                }
                            />
                        }
                    />
                }
                validation={{
                    validate: () => error,
                }}
                name="extensions"
                items={[
                    {
                        label: (
                            <span data-testid="extension-item">
                                {content?.name}
                            </span>
                        ),
                        labelDetail: (
                            <PriceComparison
                                fullPrice={formattedPrice(
                                    extension.price.value,
                                )}
                                salePrice={
                                    extensionSalePrice && !appliedToTrip
                                        ? formatCurrency(extensionSalePrice)
                                        : undefined
                                }
                            />
                        ),
                        value: extension.lineItemCode,
                    },
                ]}
                checkedValues={draftQuote?.order.trips[0].lineItems
                    .filter(
                        (lineItem) =>
                            lineItem.__typename === "TourExtensionLineItem",
                    )
                    .map((lineItem) => lineItem.lineItemCode)}
                onChange={(e) => {
                    if (!isEditing) {
                        setError(undefined);
                        if (e.target.checked) {
                            handleAddExtension(extension.lineItemCode);
                        } else {
                            handleRemoveExtension();
                        }
                    } else {
                        e.preventDefault();
                    }
                }}
            />
            {isRequired ? (
                <Text.Body style={{ marginTop: "var(--g-spacing-xs)" }}>
                    {productPage.tripBuilder.quote.extension.includedByGroupCoordinator.l(
                        { name: content?.name ?? "" },
                    )}
                </Text.Body>
            ) : null}
            {isRecommended ? (
                <Text.Body style={{ marginTop: "var(--g-spacing-xs)" }}>
                    {productPage.tripBuilder.quote.extension.recommendedByGroupCoordinator.l(
                        { name: content?.name ?? "" },
                    )}
                </Text.Body>
            ) : null}
        </div>
    );
};
